import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Money, CheckCircle, XCircle, ArrowCounterClockwise, Receipt, CaretDown, CaretRight, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { GLAccountSelect, GLPreview, usePostingMap } from "@/pages/finance/GLAccountBits";

// Expense categories — must match the backend GL posting maps.
const OVERHEAD_CATS = ["Rent & Utilities", "Travel", "Office Supplies", "Salaries & Benefits", "Insurance", "Software & Subscriptions", "Professional Fees", "Marketing", "Bank Charges", "Miscellaneous"];
const PROJECT_CATS = ["Travel & Logistics", "Materials", "Direct Labor", "Subcontractors", "Equipment", "Permits & Fees", "Testing & QA", "Site Expenses", "Consumables", "Miscellaneous"];
const BANK_KEYS = [["premier", "Premier Bank"], ["amal", "Amal Bank"], ["salaam", "Salaam Bank"], ["dahabshiil", "Dahabshiil Bank"]];

const STATUS_META = {
  pending_manager:     { label: "Awaiting manager",       tone: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_management:  { label: "Awaiting management",    tone: "bg-orange-50 text-orange-700 border-orange-200" },
  pending_finance:     { label: "Awaiting finance",       tone: "bg-blue-50 text-blue-700 border-blue-200" },
  approved_for_payment:{ label: "Ready for payment",      tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  paid:                { label: "Paid — confirm receipt", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  completed:           { label: "Completed",              tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rejected:            { label: "Rejected",               tone: "bg-red-50 text-red-700 border-red-200" },
  returned:            { label: "Returned to requester",  tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

const MGR_ROLES_MIN = 2;
const ROLE_RANK = {
  staff: 1, finance_officer: 1, hr_officer: 1, procurement_officer: 1,
  compliance_officer: 2, asset_manager: 2, finance_manager: 2, hr_manager: 2,
  procurement_manager: 2, project_manager: 2, manager: 2,
  management: 3, admin: 3, super_admin: 4,
};

function StageRail({ req }) {
  const done = { rejected: true, returned: true };
  const all = [...(req.stages || [])];
  if (req.receipt_required) all.push("receipt");
  const LABELS = { manager: "Manager", management: "Management", finance: "Finance", pay: "Payment", receipt: "Receipt" };
  const currentIdx = req.status === "paid" ? all.indexOf("receipt")
    : req.status === "completed" ? all.length
    : all.indexOf(req.current_stage);
  return (
    <div className="flex items-center gap-1" data-testid={`payreq-rail-${req.request_no}`}>
      {all.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`rounded px-1.5 py-0.5 font-mono-alt text-[9px] uppercase tracking-wider ${
            done[req.status] ? "bg-slate-100 text-slate-400"
            : i < currentIdx || req.status === "completed" ? "bg-emerald-100 text-emerald-700"
            : i === currentIdx ? "bg-[#0f1e42] text-white"
            : "bg-slate-100 text-slate-400"}`}>
            {LABELS[s]}
          </span>
          {i < all.length - 1 && <span className="text-slate-300 text-[9px]">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

function NewRequestDialog({ open, onClose, onCreated, settings }) {
  const empty = { project_id: "", department: "", purpose: "", beneficiary: "", amount: "", currency: "USD", required_date: "", urgent: false, employee_id: "", employee_name: "", category: "Miscellaneous" };
  const [form, setForm] = useState(empty);
  const [projects, setProjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setForm(empty); return; }
    api.get("/projects").then((r) => setProjects(r.data.items || r.data || [])).catch(() => {});
    api.get("/hr/staff").then((r) => setStaff(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setStaff([]));
  }, [open]); // eslint-disable-line

  const cats = form.project_id ? PROJECT_CATS : OVERHEAD_CATS;

  const amt = Number(form.amount) || 0;
  const routeHint = amt > 0 && settings
    ? amt <= settings.small_threshold && !form.urgent
      ? "Small routine payment — Manager approval, then Finance pays (2 steps)."
      : amt > settings.senior_threshold || form.urgent
        ? "Manager → Senior Management → Finance (3 approvals — high value / urgent)."
        : "Manager → Finance (2 approvals)."
    : null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payment-requests", { ...form, amount: amt });
      toast.success(`${data.request_no} submitted — ${data.next_action?.label || "awaiting manager"}`);
      onCreated(data); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New payment request</DialogTitle>
          <DialogDescription>Short form — only what's needed to pay. Approvals are routed automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>What is this payment for? *</Label>
            <Textarea rows={2} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                      placeholder="e.g. Fuel for site generator — Week 30" data-testid="payreq-purpose" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Pay to (beneficiary) *</Label>
              <Input value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })}
                     placeholder="Person or company" data-testid="payreq-beneficiary" />
            </div>
            <div>
              <Label>Amount (USD) *</Label>
              <Input type="number" step="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payreq-amount" />
            </div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v, category: "Miscellaneous" })}>
                <SelectTrigger data-testid="payreq-project"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No project (overhead)</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Needed by</Label>
              <Input type="date" value={form.required_date}
                     onChange={(e) => setForm({ ...form, required_date: e.target.value })} data-testid="payreq-date" />
            </div>
            <div>
              <Label>Requesting employee (optional)</Label>
              <Select value={form.employee_id || "none"} onValueChange={(v) => {
                if (v === "none") { setForm({ ...form, employee_id: "", employee_name: "" }); return; }
                const s = staff.find((x) => x.id === v);
                setForm({ ...form, employee_id: v, employee_name: s?.full_name || s?.name || "", beneficiary: form.beneficiary || s?.full_name || s?.name || "" });
              }}>
                <SelectTrigger data-testid="payreq-employee"><SelectValue placeholder="Not staff-related" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">— Not staff-related —</SelectItem>
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.full_name || s.name}{s.department ? ` · ${s.department}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expense category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="payreq-category"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="mt-1 text-[10px] text-slate-500">Sets which expense account the GL records when this is paid.</div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={form.urgent} onChange={(e) => setForm({ ...form, urgent: e.target.checked })}
                   data-testid="payreq-urgent" />
            Urgent / exceptional (adds a senior management check)
          </label>
          {routeHint && (
            <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-800" data-testid="payreq-route-hint">
              {routeHint}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.purpose.trim() || !form.beneficiary.trim() || amt <= 0}
                  data-testid="payreq-submit">
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayDialog({ req, onClose, onDone }) {
  const pmap = usePostingMap();
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10), method: "bank_transfer", reference: "",
    bank_key: "premier", category: req.category || "Miscellaneous", expense_account: "",
  });
  const [busy, setBusy] = useState(false);
  const isProject = !!req.project_id;
  const cats = isProject ? PROJECT_CATS : OVERHEAD_CATS;
  const defaultExp = pmap
    ? (isProject ? (pmap.project_cost?.[form.category] || "5199") : (pmap.overhead?.[form.category] || "6199"))
    : "";
  const effExp = form.expense_account || defaultExp;
  const bankCode = form.method === "cash" ? (pmap?.defaults?.cash || "1110") : (pmap?.bank?.[form.bank_key] || "1120");
  const glRows = req.amount > 0 && effExp
    ? [{ code: effExp, debit: req.amount }, { code: bankCode, credit: req.amount }]
    : [];

  const pay = async () => {
    setBusy(true);
    try {
      await api.post(`/payment-requests/${req.id}/pay`, { ...form, expense_account: effExp });
      toast.success(`${req.request_no} paid — GL journal posted`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record payment · {req.request_no}</DialogTitle>
          <DialogDescription>
            {formatCurrency(req.amount)} to {req.beneficiary}
            {req.employee_name ? ` (for ${req.employee_name})` : ""}. Posts the GL journal automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Payment date</Label>
            <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="pay-date" />
          </div>
          <div>
            <Label>Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="mobile_money">Mobile money</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.method !== "cash" && (
            <div>
              <Label>Bank paid from</Label>
              <Select value={form.bank_key} onValueChange={(v) => setForm({ ...form, bank_key: v })}>
                <SelectTrigger data-testid="pay-bank"><SelectValue /></SelectTrigger>
                <SelectContent>{BANK_KEYS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Expense category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, expense_account: "" })}>
              <SelectTrigger data-testid="pay-category"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            {req.category && <div className="mt-1 text-[10px] text-slate-500">Requested as: {req.category}</div>}
          </div>
          <div className="col-span-2">
            <Label>Expense account (debited)</Label>
            <GLAccountSelect value={effExp} onChange={(v) => setForm({ ...form, expense_account: v })} types={["expense", "asset"]} testid="pay-expense-account" />
          </div>
          <div className="col-span-2">
            <Label>Reference (optional)</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   placeholder="Transfer ref / cheque no." data-testid="pay-reference" />
          </div>
          <div className="col-span-2">
            <GLPreview rows={glRows} testid="payreq-gl-preview" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={pay} disabled={busy} data-testid="pay-confirm">{busy ? "Recording…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [paying, setPaying] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");

  const rank = ROLE_RANK[user?.role] || 1;
  const isFinance = ["finance_manager", "finance_officer", "admin", "super_admin", "management"].includes(user?.role);

  const load = () => Promise.all([
    api.get("/payment-requests"),
    api.get("/payment-requests/settings"),
  ]).then(([l, s]) => { setRows(l.data.items || []); setSettings(s.data); })
    .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const visible = useMemo(() => rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "action") return ["pending_manager", "pending_management", "pending_finance", "approved_for_payment", "paid"].includes(r.status);
    if (filter === "mine") return r.requester === user?.email;
    return r.status === filter;
  }), [rows, filter, user]);

  const decide = async (r, decision) => {
    const note = decision !== "approve" ? window.prompt(`Reason for ${decision}:`) : "";
    if (decision !== "approve" && note === null) return;
    try {
      const { data } = await api.post(`/payment-requests/${r.id}/decide`, { decision, note: note || "" });
      toast.success(`${r.request_no} — ${data.status.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmReceipt = async (r) => {
    try {
      await api.post(`/payment-requests/${r.id}/confirm-receipt`, {});
      toast.success(`${r.request_no} completed — receipt confirmed`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canDecideStage = (r) => {
    if (r.requester === user?.email && user?.role !== "super_admin") return false;
    if (r.current_stage === "manager") return rank >= MGR_ROLES_MIN;
    if (r.current_stage === "management") return rank >= 3;
    if (r.current_stage === "finance") return isFinance;
    return false;
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-6" data-testid="payment-requests-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900">Payment Requests</h1>
          <p className="text-xs text-slate-500">
            Short form · automatic routing · {settings ? `small ≤ ${formatCurrency(settings.small_threshold)} = 2 steps, senior review above ${formatCurrency(settings.senior_threshold)}` : ""}
          </p>
        </div>
        <Button className="ml-auto btn-primary-blue" onClick={() => setShowNew(true)} data-testid="payreq-new-btn">
          <Plus size={14} className="mr-1" /> New request
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {[["all", "All"], ["action", "Needs action"], ["mine", "My requests"], ["paid", "Paid"], ["completed", "Completed"], ["rejected", "Rejected"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`payreq-filter-${v}`}
            className={`rounded-full border px-3 py-1 text-xs ${filter === v ? "border-[#0f1e42] bg-[#0f1e42] text-white" : "border-slate-200 bg-white text-slate-600"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-2.5">No</th><th className="px-4 py-2.5">Purpose / Beneficiary</th>
              <th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Progress</th>
              <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-slate-400" data-testid="payreq-empty">
                No payment requests yet — click "New request" to raise one.
              </td></tr>
            )}
            {visible.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending_manager;
              return (
                <React.Fragment key={r.id}>
                  <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(openId === r.id ? null : r.id)}
                      data-testid={`payreq-row-${r.request_no}`}>
                    <td className="px-4 py-2.5 font-mono-alt text-xs">{r.request_no}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900 line-clamp-1">{r.purpose}</div>
                      <div className="text-[11px] text-slate-500">
                        → {r.beneficiary}{r.employee_name ? ` · for ${r.employee_name}` : ""}{r.category ? ` · ${r.category}` : ""}{r.project_name ? ` · ${r.project_name}` : ""}
                        {r.urgent && <span className="ml-1.5 rounded bg-red-50 px-1 text-[9px] font-semibold text-red-600 border border-red-200">URGENT</span>}
                        {r.over_budget && <span className="ml-1.5 rounded bg-orange-50 px-1 text-[9px] font-semibold text-orange-600 border border-orange-200">OVER BUDGET</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(r.amount)}</td>
                    <td className="px-4 py-2.5"><StageRail req={r} /></td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.tone}`}
                            data-testid={`payreq-status-${r.request_no}`}>{meta.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {openId === r.id ? <CaretDown size={12} className="inline text-slate-400" /> : <CaretRight size={12} className="inline text-slate-400" />}
                    </td>
                  </tr>
                  {openId === r.id && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={6} className="px-6 py-3">
                        <div className="flex flex-wrap items-start gap-6">
                          <div className="min-w-56 flex-1">
                            <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">Trail</div>
                            <ul className="mt-1 space-y-0.5">
                              {(r.trail || []).map((t, i) => (
                                <li key={i} className="flex gap-2 text-[11px] text-slate-600">
                                  <span className="font-mono-alt text-slate-400">{formatDate(t.at)}</span>
                                  <span className="font-medium">{t.action.replaceAll("_", " ")}</span>
                                  <span className="text-slate-400">{t.by}</span>
                                  {t.note && <span className="italic text-slate-500">"{t.note}"</span>}
                                </li>
                              ))}
                            </ul>
                            {r.payment && (
                              <div className="mt-1.5 text-[11px] text-emerald-700">
                                Paid {formatDate(r.payment.payment_date)} · {r.payment.method?.replaceAll("_", " ")} · GL {r.payment.journal_entry_no}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {["pending_manager", "pending_management", "pending_finance"].includes(r.status) && canDecideStage(r) && (
                              <>
                                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700"
                                        onClick={(e) => { e.stopPropagation(); decide(r, "approve"); }}
                                        data-testid={`payreq-approve-${r.request_no}`}>
                                  <CheckCircle size={12} className="mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); decide(r, "return"); }}
                                        data-testid={`payreq-return-${r.request_no}`}>
                                  <ArrowCounterClockwise size={12} className="mr-1" /> Return
                                </Button>
                                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
                                        onClick={(e) => { e.stopPropagation(); decide(r, "reject"); }}
                                        data-testid={`payreq-reject-${r.request_no}`}>
                                  <XCircle size={12} className="mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {r.status === "approved_for_payment" && isFinance && (
                              <Button size="sm" className="btn-primary-blue"
                                      onClick={(e) => { e.stopPropagation(); setPaying(r); }}
                                      data-testid={`payreq-pay-${r.request_no}`}>
                                <Money size={12} className="mr-1" /> Record payment
                              </Button>
                            )}
                            {r.status === "paid" && (r.requester === user?.email || rank >= 2) && (
                              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                      onClick={(e) => { e.stopPropagation(); confirmReceipt(r); }}
                                      data-testid={`payreq-receipt-${r.request_no}`}>
                                <Receipt size={12} className="mr-1" /> Confirm receipt
                              </Button>
                            )}
                            {r.status === "returned" && r.requester === user?.email && (
                              <Button size="sm" variant="outline"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try { await api.post(`/payment-requests/${r.id}/resubmit`, {}); toast.success("Resubmitted"); load(); }
                                        catch (err) { toast.error(formatApiError(err)); }
                                      }}
                                      data-testid={`payreq-resubmit-${r.request_no}`}>
                                Resubmit
                              </Button>
                            )}
                            {r.next_action?.label && !["completed", "rejected", "returned"].includes(r.status) && !canDecideStage(r) && r.status !== "approved_for_payment" && r.status !== "paid" && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                <Warning size={12} /> Next: {r.next_action.label}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewRequestDialog open={showNew} onClose={() => setShowNew(false)} onCreated={() => load()} settings={settings} />
      {paying && <PayDialog req={paying} onClose={() => setPaying(null)} onDone={load} />}
    </div>
  );
}
